import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Scale, ClipboardList, Home, HandHeart, BarChart3, Users } from "lucide-react";
import { TEAM_GROUP_URL, INHERITED_CARD_HOME_URL } from "@/lib/assets";

const SERVICES = [
  {
    icon: Scale,
    title: "When Is Probate Needed?",
    desc: "We diagnose whether a formal probate is required, or if a streamlined transfer is available — saving families months and thousands.",
    image: "https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoYW5kc2hha2UlMjBvZmZpY2V8ZW58MHx8fHwxNzc5MzgwNzk0fDA&ixlib=rb-4.1.0&q=85",
    tag: "Legal Pathway",
    to: "/when-probate-needed",
  },
  {
    icon: ClipboardList,
    title: "Duties of Executors / Administrators",
    desc: "A concierge checklist for every fiduciary duty — inventory, notice, accounting, distribution — explained without the legalese.",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80",
    tag: "Estate Management",
    to: "/probates",
  },
  {
    icon: Home,
    title: "How to Sell an Inherited Property",
    desc: "Luxury-grade marketing, court-compliant offers, and concierge-staging — orchestrated for the highest net to the estate.",
    image: INHERITED_CARD_HOME_URL,
    tag: "Property Sale",
    to: "/steps-to-sell-inherited-home",
  },
  {
    icon: HandHeart,
    title: "Full Support Network",
    desc: "Vetted attorneys, accountants, gardeners, cleaners, movers, and estate-sale specialists — a single point of contact for everything.",
    image: "https://images.unsplash.com/photo-1758612898305-7c9e13ee21b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBnZW5lcmF0aW9uJTIwaGFwcHl8ZW58MHx8fHwxNzc5MzgwNzk0fDA&ixlib=rb-4.1.0&q=85",
    tag: "Referral Network",
    to: "/referral-services",
  },
  {
    icon: BarChart3,
    title: "Marketing In The Digital Age",
    desc: "Cinematic listings, paid digital, social, and a proprietary buyer database — your property gets seen by the right people.",
    image: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=1200&q=80",
    tag: "Modern Reach",
    to: "/market-your-home",
  },
  {
    icon: Users,
    title: "Who We Are",
    desc: "A boutique team led by a probate-certified specialist, anchored in integrity, compassion, and care — backed by a national network.",
    image: TEAM_GROUP_URL,
    tag: "Our Promise",
    to: "/about",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="section-overline">What We Offer</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight tracking-tight max-w-2xl">
              A full-service probate <em className="italic text-gold-600">real estate</em> practice.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`service-card-${i}`}
              className="group relative overflow-hidden rounded-2xl border border-navy-100 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/10 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-800">
                  <s.icon size={12} className="text-gold-600" />
                  {s.tag}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl text-navy-900 leading-snug">{s.title}</h3>
                <p className="mt-2.5 text-sm text-navy-600 leading-relaxed">{s.desc}</p>
                <Link
                  to={s.to || "/contact"}
                  data-testid={`service-learn-more-${i}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-800 hover:text-gold-600 transition-colors group/btn"
                >
                  Learn More
                  <ArrowUpRight size={15} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
